import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, FlaskConical, Upload } from "lucide-react";
import { TabHeader } from "@/components/shared/TabHeader";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { ProductSectionEmptyState } from "@/components/product-detail/ProductSectionEmptyState";
import { FilterDropdown } from "@/components/shared/FilterDropdown";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import type { HeroAbTestUpdatePayload } from "@/resources/product/product.api";
import type { ABTest, TestOutcome } from "@/types/ab-tests";
import { HeroAsinCard } from "../HeroAsinCard";
import { toast } from "@/hooks/use-toast";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { productApi } from "@/resources/product";
import { keywordBankRunApi } from "@/resources/keyword-bank-run";
import { isKeywordBankImporting } from "@/utils/keywordBankImporting";
import { handleApiError } from "@/utils";
import { UploadPastTestModal } from "../UploadPastTestModal";
import { RoutePath } from "@/routes";
import type { CreateABTestLocationState } from "@/pages/CreateABTest";
import { FILTER_COLUMNS } from "./constants";
import { ABTestStatsBanner } from "./ABTestStatsBanner";

interface ABTestsContentV2Props {
  productId: number;
  heroAsin: string;
}

export function ABTestsContentV2({ productId, heroAsin: _heroAsin }: ABTestsContentV2Props) {
  const readOnly = useViewerReadOnly();
  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();
  const { data: heroAsinData, isPending } = productApi.useGetHeroProductABTests(String(productId), selectedBrandId);
  const { data: keywordBankRunResponse } = keywordBankRunApi.useGetKeywordBankRun(productId);
  const isImporting = isKeywordBankImporting(keywordBankRunResponse?.run);
  const updateTestMutation = productApi.useUpdateHeroAbTest();
  const deleteTestMutation = productApi.useDeleteHeroAbTest();
  const uploadResultsMutation = productApi.useUploadHeroAbTestResults();

  const [contentFilters, setContentFilters] = useState<Record<string, string[]>>({ status: [], result: [], scope: [] });
  const [search, setSearch] = useState("");
  const [uploadPastTestOpen, setUploadPastTestOpen] = useState(false);

  const heroAsins = useMemo(() => (heroAsinData ? [heroAsinData] : []), [heroAsinData]);
  const totalTestCount = heroAsinData?.tests.length ?? 0;

  const productStats = useMemo(() => {
    const tests = heroAsinData?.tests ?? [];
    return {
      total: tests.length,
      running: tests.filter((t) => t.status === "running").length,
      totalCvrLift: 0,
      totalSalesLift: 0,
      annualizedSales: 0,
    };
  }, [heroAsinData]);

  const navigateToCreate = useCallback(
    (options?: { prefillFromDraft?: ABTest | null }) => {
      const asin = heroAsins[0];
      if (!asin) return;
      const state: CreateABTestLocationState = {
        heroAsin: asin,
        productId,
        prefillFromDraft: options?.prefillFromDraft ?? null,
        returnTo: window.location.pathname + window.location.search,
      };
      navigate(RoutePath.CreateABTest, { state });
    },
    [heroAsins, productId, navigate],
  );

  const handleCreateTest = (_asinId: string) => navigateToCreate();
  const handleResumeDraft = (test: ABTest) => navigateToCreate({ prefillFromDraft: test });

  const handleDeleteTest = async (_asinId: string, testId: string) => {
    if (selectedBrandId == null) {
      toast({ title: "Brand required", description: "Select a brand before deleting a test.", variant: "destructive" });
      return;
    }
    try {
      await deleteTestMutation.mutateAsync({ productId, testId, brandId: selectedBrandId });
      toast({ title: "Test deleted", description: "The A/B test has been removed." });
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleDeleteTests = async (_asinId: string, testIds: string[]) => {
    if (selectedBrandId == null) {
      toast({ title: "Brand required", description: "Select a brand before deleting tests.", variant: "destructive" });
      return;
    }
    try {
      for (const testId of testIds) {
        await deleteTestMutation.mutateAsync({ productId, testId, brandId: selectedBrandId });
      }
      toast({ title: "Tests deleted", description: `${testIds.length} ${testIds.length === 1 ? "test has" : "tests have"} been removed.` });
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleUpdateTest = async (args: { productId: number; testId: string; body: HeroAbTestUpdatePayload }) => {
    if (selectedBrandId == null) {
      toast({ title: "Brand required", description: "Select a brand before saving.", variant: "destructive" });
      throw new Error("Brand required");
    }
    try {
      await updateTestMutation.mutateAsync({ productId: args.productId, testId: args.testId, body: { ...args.body, brand_id: selectedBrandId } });
    } catch (e) {
      handleApiError(e);
      throw e;
    }
  };

  const handleUploadResults = async (testId: string, file: File, outcome?: TestOutcome, variantBSwitchDate?: string) => {
    if (selectedBrandId == null) {
      toast({ title: "Brand required", description: "Select a brand before uploading results.", variant: "destructive" });
      return;
    }
    try {
      const res = await uploadResultsMutation.mutateAsync({ productId, testId, brandId: selectedBrandId, file, outcome, variantBSwitchDate });
      if (res.parsed) {
        toast({ title: "Results imported", description: `Metrics saved. Outcome: ${res.outcome}.` });
      } else {
        toast({ title: "Test marked complete", description: res.message || "The CSV could not be parsed. Outcome set to Inconclusive.", variant: "destructive" });
      }
    } catch (e) {
      handleApiError(e);
    }
  };

  const filteredAsins = heroAsins
    .map((asin) => {
      let tests = asin.tests ?? [];
      if (contentFilters.scope.length > 0) {
        tests = tests.filter((test) => {
          const fields = test.scopeFields ?? [test.contentType];
          const hasTitle = fields.includes("title");
          const hasBullets = fields.some((f) => f.startsWith("bullet"));
          if (contentFilters.scope.includes("title") && hasTitle) return true;
          if (contentFilters.scope.includes("bullets") && hasBullets) return true;
          return false;
        });
      }
      if (contentFilters.status.length > 0) {
        tests = tests.filter((test) => {
          const displayStatus = test.status === "building" ? "building" : test.status === "draft" || test.status === "paused" ? "planning" : test.status === "completed" ? "complete" : test.status;
          return contentFilters.status.includes(displayStatus);
        });
      }
      if (contentFilters.result.length > 0) {
        tests = tests.filter((test) => test.outcome && contentFilters.result.includes(test.outcome));
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        tests = tests.filter(
          (test) =>
            (test.targetKeyword ?? "").toLowerCase().includes(q) ||
            (test.targetCluster ?? "").toLowerCase().includes(q) ||
            test.intentShift.from.toLowerCase().includes(q) ||
            test.intentShift.to.toLowerCase().includes(q),
        );
      }
      return { ...asin, tests };
    })
    .filter((asin) =>
      asin.tests.length > 0 ||
      (contentFilters.scope.length === 0 && contentFilters.status.length === 0 && contentFilters.result.length === 0 && !search.trim()),
    );

  const createTestButton = (disabled: boolean) => (
    <Button disabled={disabled} onClick={() => navigateToCreate()}>
      <Plus className="h-4 w-4" />
      Create Test
    </Button>
  );

  const uploadPastTestMenuItem = (disabled: boolean) =>
    readOnly ? undefined : (
      <DropdownMenuItem disabled={disabled} onClick={() => setUploadPastTestOpen(true)}>
        <Upload className="h-4 w-4" />
        Upload past test
      </DropdownMenuItem>
    );

  if (!isPending && totalTestCount === 0) {
    // AUT-196 follow-up: while the product's keyword bank is still importing,
    // the create action is not yet meaningful -- offer it disabled with a
    // "please wait" state rather than the standard empty state.
    if (isImporting) {
      return (
        <div className="min-w-0 space-y-6">
          <TabHeader title="A/B Tests" menuItems={uploadPastTestMenuItem(true)}>
            {!readOnly && createTestButton(true)}
          </TabHeader>
          <ProductSectionEmptyState
            icon={FlaskConical}
            title="No A/B tests created yet."
            description="Please wait for the product to fully load before creating an A/B test."
            actions={[{ label: "Create A/B Test", icon: Plus, onClick: () => navigateToCreate(), disabled: true }]}
          />
        </div>
      );
    }
    return (
      <div className="min-w-0 space-y-6">
        <TabHeader title="A/B Tests">{!readOnly && createTestButton(false)}</TabHeader>
        <ProductSectionEmptyState
          icon={FlaskConical}
          title="No A/B tests created yet."
          description="Create a test to compare listing variants and measure the impact on conversion."
          actions={[{ label: "Create A/B Test", icon: Plus, onClick: () => navigateToCreate() }]}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0">
      <TabHeader title="A/B Tests" menuItems={uploadPastTestMenuItem(isPending)}>
        {!readOnly && createTestButton(isPending)}
      </TabHeader>

      <div className="flex items-center gap-3">
        <ClearableInput
          value={search}
          onChange={setSearch}
          placeholder="Search tests..."
          className="h-9"
          wrapperClassName="w-64"
        />
        <FilterDropdown columns={FILTER_COLUMNS} activeFilters={contentFilters} onFiltersChange={setContentFilters} />
      </div>

      {!isPending && totalTestCount > 0 && <ABTestStatsBanner stats={productStats} />}

      <div className="space-y-4">
        {isPending && (
          <div className="text-center py-12 text-muted-foreground">
            <p>Loading A/B tests...</p>
          </div>
        )}
        {filteredAsins?.map((heroAsin, index) => (
          <HeroAsinCard
            key={heroAsin.id}
            heroAsin={heroAsin}
            defaultOpen={index === 0}
            productId={productId}
            brandId={selectedBrandId}
            onCreateTest={handleCreateTest}
            onDeleteTest={(testId) => handleDeleteTest(heroAsin.id, testId)}
            onDeleteTests={(testIds) => handleDeleteTests(heroAsin.id, testIds)}
            onUploadResults={handleUploadResults}
            onUpdateTest={handleUpdateTest}
            onResumeDraft={handleResumeDraft}
            isUpdateTestPending={updateTestMutation.isPending}
            readOnly={readOnly}
          />
        ))}
        {!isPending && filteredAsins.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>No tests match the current filters.</p>
          </div>
        )}
      </div>

      {selectedBrandId && !readOnly && (
        <UploadPastTestModal open={uploadPastTestOpen} onOpenChange={setUploadPastTestOpen} productId={productId} brandId={selectedBrandId} />
      )}
    </div>
  );
}
